import { useEffect, useState } from "react";
import { MarkAsSeen } from "@/api/contactUs";
import { ContactFormState } from "@/interfaces/contactUs_type";

export type ContactUsCardProps = {
	contactUs: ContactFormState;
	isNew: boolean;
	onView: () => void;
};

/**
 * Contact Us Card
 * @param contactUs - The contact us object
 * @param isNew - Whether the contact us is new
 * @param onView - The function to call when the contact us is viewed
 * @returns A contact us component
 */
export function ContactUsCard({
	contactUs,
	isNew,
	onView,
}: ContactUsCardProps) {
	const [isNewLocal, setIsNewLocal] = useState(isNew);
	const [disabled, setDisabled] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);

	const isLongMessage = contactUs.message.length > 50;

	useEffect(() => {
		if (isNewLocal) {
			setIsNewLocal(true);
			onView();
		}
	}, [isNewLocal, onView]);

	const handleMarkAsSeen = async () => {
		const response = await MarkAsSeen(contactUs.email);
		if (response.operation === "COMPLETED") {
			setIsNewLocal(false);
			setDisabled(true);
		}
	};

	const handleToggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	if (disabled) {
		return null;
	}

	return (
		<div className="min-w-[280px] max-w-[320px] transition-all duration-300 hover:shadow-lg border-2 border-orange-200 rounded-md p-2 bg-orange-50 hover:bg-orange-100">
			<div className="space-y-4 p-2 flex flex-col h-full">
				<CardHeader email={contactUs.email} isNew={isNewLocal} />

				<MessageContent
					message={contactUs.message}
					isExpanded={isExpanded}
					isLongMessage={isLongMessage}
					onToggleExpand={handleToggleExpand}
				/>

				<MarkAsSeenButton onClick={handleMarkAsSeen} />
			</div>
		</div>
	);
}

const EmailIcon = () => (
	<svg
		className="w-5 h-5 text-blue-500"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
		/>
	</svg>
);

const MessageIcon = () => (
	<svg
		className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
		/>
	</svg>
);

const CloseIcon = () => (
	<svg
		className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M6 18L18 6M6 6l12 12"
		/>
	</svg>
);

const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
	<svg
		className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M19 9l-7 7-7-7"
		/>
	</svg>
);

const CheckIcon = () => (
	<svg
		className="w-4 h-4"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M5 13l4 4L19 7"
		/>
	</svg>
);

const NewBadge = () => (
	<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
		<div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse"></div>
		New
	</span>
);

const CardHeader = ({ email, isNew }: { email: string; isNew: boolean }) => (
	<div className="flex items-center justify-between gap-3">
		<IconAndEmail email={email} />

		{isNew && (
			<div className="flex-shrink-0">
				<NewBadge />
			</div>
		)}
	</div>
);

const IconAndEmail = ({ email }: { email: string }) => {
	return (
		<div className="flex items-center gap-2 min-w-0 flex-1">
			<div className="flex-shrink-0">
				<EmailIcon />
			</div>

			<div className="min-w-0 flex-1">
				<a href={`mailto:${email}`} className="hover:underline">
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
						{email}
					</h3>
				</a>

				<p className="text-xs text-gray-500 dark:text-gray-400">
					Contact Request
				</p>
			</div>
		</div>
	);
};

const MessagePopup = ({
	message,
	onClose,
}: {
	message: string;
	onClose: () => void;
}) => (
	<>
		{/* Backdrop */}
		<div
			className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
			onClick={(e) => {
				e.stopPropagation();
				onClose();
			}}
		/>

		{/* Popup container */}
		<div className="absolute top-0 left-0 z-50 min-w-[300px] max-w-[400px] bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-600 p-4 transform transition-all duration-300 ease-out">
			{/* Header */}
			<div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-600">
				<h4 className="text-sm font-semibold text-gray-900 dark:text-white">
					Full Message
				</h4>
				<button
					onClick={(e) => {
						e.stopPropagation();
						onClose();
					}}
					className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
				>
					<CloseIcon />
				</button>
			</div>

			{/* Content */}
			<div className="max-h-[60vh] overflow-y-auto">
				<p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed break-words whitespace-pre-wrap">
					{message}
				</p>
			</div>
		</div>
	</>
);

const MessageContent = ({
	message,
	isExpanded,
	isLongMessage,
	onToggleExpand,
}: {
	message: string;
	isExpanded: boolean;
	isLongMessage: boolean;
	onToggleExpand: () => void;
}) => (
	<div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex-1">
		<div className="flex items-start gap-2">
			<MessageIcon />
			<div className="flex-1 min-w-0 relative">
				{/* Collapsed message view */}
				<div className={`${isExpanded ? "invisible" : "visible"}`}>
					<p
						className={`text-sm w-[200px] text-gray-700 dark:text-gray-300 leading-relaxed break-words ${
							!isExpanded && isLongMessage ? "line-clamp-3" : ""
						}`}
					>
						{message}
					</p>
				</div>

				{/* Expanded popup overlay */}
				{isExpanded && (
					<MessagePopup message={message} onClose={onToggleExpand} />
				)}

				{/* Expand/collapse button */}
				{isLongMessage && (
					<button
						onClick={(e) => {
							e.stopPropagation();
							onToggleExpand();
						}}
						className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-all duration-200 hover:scale-105"
					>
						<ChevronIcon isExpanded={isExpanded} />
						{isExpanded ? "Close" : "Read more"}
					</button>
				)}
			</div>
		</div>
	</div>
);

const MarkAsSeenButton = ({ onClick }: { onClick: () => void }) => (
	<div className="pt-2">
		<button
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}
			className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-lg shadow-sm transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
		>
			<CheckIcon />
			Mark as Seen
		</button>
	</div>
);

